import React, { createContext, useState, useContext, useEffect } from 'react';
import { getApiBase } from '../backendProbe.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Back-compat: older code used 'authToken' while newer used 'token'
    return localStorage.getItem('authToken') || localStorage.getItem('token') || null;
  });
  const [userPlan, setUserPlan] = useState(null);

  // Fetch user plan from backend
  const fetchUserPlan = async (authToken) => {
    if (!authToken) {
      setUserPlan(null);
      return;
    }

    try {
      const base = getApiBase();
      const response = await fetch(base ? `${base}/auth/user/plan` : '/auth/user/plan', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const planData = await response.json();
        setUserPlan(planData.plan || 'hobby');
      } else if (response.status === 401) {
        // Token is invalid for the currently configured backend (common when mixing local vs cloud).
        // Clear auth to stop repeated 401 spam and force a clean re-login.
        console.warn('Auth token rejected (401). Clearing stored token. Check API base (REACT_APP_API_URL, runtime-config) and backend.');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        setUser(null);
        setUserPlan(null);
        setToken(null);
      } else {
        // Default to hobby if fetch fails
        setUserPlan('hobby');
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
      setUserPlan('hobby'); // Default to hobby on error
    }
  };

  useEffect(() => {
    if (token) {
      try {
        // Decode JWT token to get user info
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          email: payload.email,
          name: payload.name || payload.username,
          username: payload.username,
          userId: payload.userId,
        });
        // Fetch user plan
        fetchUserPlan(token);
      } catch (e) {
        // If token decode fails, set generic user
        setUser({ username: 'authenticated_user' });
        setUserPlan('hobby');
      }
    } else {
      setUser(null);
      setUserPlan(null);
    }
  }, [token]);

  const login = (newToken, userData = null) => {
    setToken(newToken);
    // Persist under both keys for compatibility across the app
    localStorage.setItem('token', newToken);
    localStorage.setItem('authToken', newToken);
    
    // Decode token to get user info
    if (newToken) {
      try {
        const payload = JSON.parse(atob(newToken.split('.')[1]));
        setUser({
          email: payload.email || userData?.email,
          name: payload.name || payload.username || userData?.name,
          username: payload.username || userData?.username,
          userId: payload.userId,
          ...userData
        });
        // Fetch user plan after login
        fetchUserPlan(newToken);
      } catch (e) {
        // If token decode fails, use provided userData or set generic user
        setUser(userData || { username: 'authenticated_user' });
        setUserPlan('hobby');
      }
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    setUser(null);
    setUserPlan(null);
  };

  const refreshPlan = () => {
    if (token) {
      fetchUserPlan(token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, userPlan, login, logout, refreshPlan }}>
      {children}
    </AuthContext.Provider>
  );
};

const defaultAuth = {
  user: null,
  token: null,
  userPlan: null,
  login: () => {},
  logout: () => {},
  refreshPlan: () => {},
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  return ctx ?? defaultAuth;
};
